import { getHashBaseUrl } from '#app/utils/misc.tsx'
import TypographyParagraph from '../typography/TypographyParagraph'

const OffchainSale = ({
	metadata,
	asCountersign = false,
}: {
	metadata: any
	asCountersign?: boolean
}) => {
	const truncateStringInMiddle = (str: string) => {
		if (!str) return ''
		if (!isValidEthereumAddress(str) && !isValidSolanaAddress(str)) return str
		if (str.length > 8) {
			return (
				str.substring(0, 4) + '...' + str.substring(str.length - 4, str.length)
			)
		} else {
			return str
		}
	}

	function getMetadataChain(metadata: any) {
		const references = metadata.references.entry
			? metadata.references.entry
			: metadata.references
		const chain = references.chain ? references.chain.name : false
		return chain
	}

	const isValidEthereumAddress = (address: string) => {
		const regex = /^(0x)?[0-9a-fA-F]{40}$/
		return regex.test(address)
	}

	const isValidSolanaAddress = (address: string) => {
		const regex = /^[1-9A-HJ-NP-Za-km-z]{44}$/
		return regex.test(address)
	}

	const chain = getMetadataChain(metadata)
	const hashBaseUrl = getHashBaseUrl(chain)

	return (
		<div className="flex flex-col">
			{metadata.properties.description && (
				<div
					className={`${asCountersign ? 'border-b border-textInactiveLight px-4 py-2 dark:border-textInactiveDark' : 'mb-4'}`}
				>
					<TypographyParagraph
						size="medium"
						isBold={true}
						additionalClasses="mb-4 text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{metadata.properties.description}
					</TypographyParagraph>
				</div>
			)}
			<div
				className={`flex h-full w-full flex-col rounded bg-uiTertiaryLight dark:bg-uiTertiaryDark ${asCountersign ? '' : 'border border-textInactiveLight dark:border-textInactiveDark'}`}
			>
				<div className="flex flex-col sm:flex-row">
					<div
						id="date"
						className="flex w-full flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark md:flex-row"
					>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Date:
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-1"
						>
							{metadata.properties.date}
						</TypographyParagraph>
					</div>
				</div>
				{metadata.properties.url && (
					<div className="flex flex-col sm:flex-row">
						<div
							id="url"
							className="flex w-full flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark md:flex-row"
						>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Link:
							</TypographyParagraph>
							<a
								href={metadata.properties.url}
								target="_blank"
								rel="noreferrer"
							>
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-1"
								>
									{metadata.properties.url}
								</TypographyParagraph>
							</a>
						</div>
					</div>
				)}
				{metadata.properties.transactionHash && (
					<div className="flex flex-col sm:flex-row">
						<div
							id="transactionHash"
							className="flex w-full flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark md:flex-row"
						>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Transaction Hash:
							</TypographyParagraph>
							<a
								href={
									metadata.properties.transactionHash.includes('https://')
										? metadata.properties.transactionHash
										: `${hashBaseUrl}${metadata.properties.transactionHash}`
								}
								target="_blank"
								rel="noreferrer"
							>
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-1 break-all"
								>
									{truncateStringInMiddle(metadata.properties.transactionHash)}
								</TypographyParagraph>
							</a>
						</div>
					</div>
				)}
				<div className="flex flex-col sm:flex-row">
					<div
						id="buyerAddress"
						className="flex flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r md:flex-row"
					>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Buyer:
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all ml-1"
						>
							{truncateStringInMiddle(metadata.properties.receiverAddress)}
						</TypographyParagraph>
					</div>
					<div
						id="sellerAddress"
						className="flex flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 md:flex-row"
					>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Seller:
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all ml-1"
						>
							{truncateStringInMiddle(metadata.properties.ownerAddress)}
						</TypographyParagraph>
					</div>
				</div>
				<div className="flex flex-col sm:flex-row">
					<div
						id="amount"
						className="flex flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r md:flex-row"
					>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Amount:
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-1"
						>
							{metadata.properties.amount}
						</TypographyParagraph>
					</div>
					<div
						id="currency"
						className="flex flex-col justify-between border-b border-textInactiveLight px-4 py-2 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 md:flex-row"
					>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Currency:
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-1"
						>
							{metadata.properties.currency}
						</TypographyParagraph>
					</div>
				</div>
			</div>
		</div>
	)
}

export default OffchainSale
